import {
  CheckCircle2,
  FileText,
  Fuel,
  Gauge,
  Pencil,
  Plus,
  Printer,
  Receipt,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { mostrarPdfAutenticado } from '@/lib/descargas';
import { formatCurrency, formatDateTimeValue } from '@/lib/utils';

import {
  turnoApi,
  useBuscarLineasCredito,
  useCatalogosPos,
  useActualizarDireccionClientePos,
  useCrearComprobantePos,
  useCrearVentaCredito,
  useConsultarDni,
  useRegistrarCalibracion,
  useRegistrarConsumoInterno,
  useValidarCodigoCalibracion,
} from '../turno.api';
import type {
  PosCatalogosRespuesta,
  PosComprobanteRespuesta,
  PosVentaCreditoRespuesta,
} from '../turno.types';

/** Tipos de venta que se pueden registrar desde esta pantalla. */
type ModoPos = 'comprobante' | 'credito' | 'consumo' | 'calibracion';

/** Resultado de una conversión de transacción (consumo interno o calibración). */
interface ResultadoConversion {
  tipo: 'consumo' | 'calibracion';
  id: number;
  mensaje: string;
}

function mensajeError(error: unknown): string {
  return error instanceof ApiError
    ? error.message
    : error instanceof Error
      ? error.message
      : 'No se pudo crear el comprobante.';
}

function redondearDinero(valor: number): number {
  return Math.round((valor + Number.EPSILON) * 100) / 100;
}

export function PosComprobantePageContent({
  onClose,
  onVolverAlComprobante,
  modoInicial = 'comprobante',
}: {
  onClose: () => void;
  /**
   * Vuelve a esta misma pantalla (formulario limpio) para registrar otra operación
   * en lugar de salir al turno. Si no se indica, el botón sale al turno.
   */
  onVolverAlComprobante?: (modo: ModoPos) => void;
  /** Tipo de venta con el que abre la pantalla (`?modo=`). */
  modoInicial?: ModoPos;
}) {
  useDocumentTitle('Crear comprobante · Mi turno');
  const [textoBusquedaCliente, setTextoBusquedaCliente] = useState('');
  const [buscarCliente, setBuscarCliente] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [clienteSeleccionado, setClienteSeleccionado] = useState<
    PosCatalogosRespuesta['clientes'][number] | null
  >(null);
  const [modo, setModo] = useState<ModoPos>(modoInicial);
  const [creditoId, setCreditoId] = useState('');
  const [busquedaPlacaCredito, setBusquedaPlacaCredito] = useState('');
  const [busquedaRucCredito, setBusquedaRucCredito] = useState('');
  const [conductorDniCredito, setConductorDniCredito] = useState('');
  const [conductorNombreCredito, setConductorNombreCredito] = useState('');
  const [kilometrajeCredito, setKilometrajeCredito] = useState('');
  const [valeCredito, setValeCredito] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState<'01' | '03' | '99'>('03');
  const [serieId, setSerieId] = useState('');
  const [dialogoDireccionAbierto, setDialogoDireccionAbierto] = useState(false);
  const [direccionEditada, setDireccionEditada] = useState('');
  const [metodoPagoId, setMetodoPagoId] = useState('');
  const [montoPagoPrimario, setMontoPagoPrimario] = useState('');
  const [metodoPagoSecundarioId, setMetodoPagoSecundarioId] = useState('');
  const [montoPagoSecundario, setMontoPagoSecundario] = useState('');
  const [mostrarMetodoSecundario, setMostrarMetodoSecundario] = useState(false);
  const [bancoId, setBancoId] = useState('');
  const [bancoSecundarioId, setBancoSecundarioId] = useState('');
  const [referencia, setReferencia] = useState('');
  const [referenciaSecundaria, setReferenciaSecundaria] = useState('');
  const [placa, setPlaca] = useState('');
  const [aplicarDescuento, setAplicarDescuento] = useState(true);
  const [productoId, setProductoId] = useState('');
  const [productos, setProductos] = useState<{ producto_id: number; cantidad: number }[]>([]);
  const [transacciones, setTransacciones] = useState<number[]>([]);
  const [resultado, setResultado] = useState<PosComprobanteRespuesta | null>(null);
  const [resultadoCredito, setResultadoCredito] = useState<PosVentaCreditoRespuesta | null>(null);
  const [dialogoFormatoAbierto, setDialogoFormatoAbierto] = useState(false);
  const [resultadoModalAbierto, setResultadoModalAbierto] = useState(false);
  const [formatoImpresion, setFormatoImpresion] = useState<'ticket' | 'a4'>('ticket');
  const [errorPdf, setErrorPdf] = useState('');
  const [dniConsumo, setDniConsumo] = useState('');
  const [conductorConsumo, setConductorConsumo] = useState('');
  const [placaConsumo, setPlacaConsumo] = useState('');
  const [kilometrajeConsumo, setKilometrajeConsumo] = useState('');
  const [observacionesConsumo, setObservacionesConsumo] = useState('');
  const [codigoCalibracion, setCodigoCalibracion] = useState('');
  const [resultadoConversion, setResultadoConversion] = useState<ResultadoConversion | null>(null);
  const clienteInicializado = useRef(false);

  useEffect(() => {
    const temporizador = window.setTimeout(
      () => setBuscarCliente(textoBusquedaCliente.trim()),
      250,
    );

    return () => window.clearTimeout(temporizador);
  }, [textoBusquedaCliente]);

  const catalogos = useCatalogosPos(
    buscarCliente,
    !resultado && !resultadoCredito && resultadoConversion === null,
    Number(clienteId) || undefined,
    modo === 'comprobante' ? tipoDocumento : undefined,
    placa.trim().toUpperCase(),
  );
  const crear = useCrearComprobantePos();
  const crearCredito = useCrearVentaCredito();
  const buscarLinea = useBuscarLineasCredito();
  const consultarDni = useConsultarDni();
  const actualizarDireccion = useActualizarDireccionClientePos();
  const validarCodigo = useValidarCodigoCalibracion();
  const registrarConsumo = useRegistrarConsumoInterno();
  const registrarCalibracion = useRegistrarCalibracion();
  const datos = catalogos.data;
  const seriesDisponibles = datos?.series ?? [];
  const serieSeleccionada =
    seriesDisponibles.find((serie) => String(serie.id) === serieId) ?? seriesDisponibles[0];
  const metodoContado = datos?.metodos_pago.find(
    (metodo) => metodo.nombre.trim().toUpperCase() === 'CONTADO',
  );
  const metodoPagoIdActual = metodoPagoId || (metodoContado ? String(metodoContado.id) : '');
  const clienteActual =
    datos?.clientes.find((cliente) => cliente.id === Number(clienteId)) ??
    (datos?.cliente_seleccionado?.id === Number(clienteId)
      ? datos.cliente_seleccionado
      : null) ??
    clienteSeleccionado;
  const clientesVisibles = (datos?.clientes ?? []).filter(
    (cliente) =>
      modo !== 'comprobante' ||
      tipoDocumento !== '01' ||
      (cliente.tipo_documento === '6' && /^\d{11}$/.test(cliente.numero_documento ?? '')),
  );
  const opcionesClientes =
    clienteActual && !clientesVisibles.some((cliente) => cliente.id === clienteActual.id)
      ? [clienteActual, ...clientesVisibles]
      : clientesVisibles;
  /** Líneas de crédito ubicadas por placa o RUC (nunca se listan todas). */
  const lineasCredito = (buscarLinea.data?.lineas ?? []).filter(
    (credito) => !credito.requiere_factura,
  );
  const clienteCredito = buscarLinea.data?.cliente ?? null;
  /** Línea de crédito elegida; el cliente y el límite salen de la propia línea. */
  const lineaCredito = lineasCredito.find((credito) => String(credito.id) === creditoId);
  const creditoTransaccion =
    transacciones.length === 1
      ? datos?.transacciones.find((fila) => fila.id === transacciones[0])
      : undefined;
  const descuentoLineaUnitario =
    lineaCredito?.aplicar_descuento && creditoTransaccion
      ? (lineaCredito.descuentos.find((fila) => fila.producto_id === creditoTransaccion.producto_id)
          ?.descuento ??
        lineaCredito.descuentos.find((fila) => fila.producto_id === 0)?.descuento ??
        0)
      : 0;
  const totalCredito = creditoTransaccion
    ? redondearDinero(
        creditoTransaccion.monto -
          redondearDinero(descuentoLineaUnitario * creditoTransaccion.cantidad),
      )
    : 0;
  const lineaCompatible =
    lineaCredito === undefined ||
    lineaCredito.producto_id === 0 ||
    lineaCredito.producto_id === creditoTransaccion?.producto_id;
  const kilometrajeNumeroCredito =
    kilometrajeCredito.trim() === '' ? null : Number(kilometrajeCredito);
  const descuentosPorProducto = new Map<number, number>();
  for (const descuento of datos?.descuentos ?? []) {
    if (!descuentosPorProducto.has(descuento.producto_id)) {
      descuentosPorProducto.set(descuento.producto_id, descuento.descuento);
    }
  }
  const descuentoUnitario = (productoId: number) =>
    aplicarDescuento ? descuentosPorProducto.get(productoId) ?? 0 : 0;
  const precioConDescuento = (productoId: number, precio: number) =>
    redondearDinero(precio - descuentoUnitario(productoId));
  const totalTransacciones = (datos?.transacciones ?? [])
    .filter((fila) => transacciones.includes(fila.id))
    .reduce((suma, fila) => {
      const descuento = redondearDinero(
        descuentoUnitario(fila.producto_id) * fila.cantidad,
      );
      return suma + redondearDinero(fila.monto - descuento);
    }, 0);
  const totalProductos = productos.reduce((suma, item) => {
    const producto = datos?.productos.find((fila) => fila.id === item.producto_id);
    return (
      suma +
      redondearDinero(
        precioConDescuento(item.producto_id, producto?.precio ?? 0) * item.cantidad,
      )
    );
  }, 0);
  const total = redondearDinero(totalTransacciones + totalProductos);
  const descuentoExcedePrecio =
    (datos?.transacciones ?? []).some(
      (fila) =>
        transacciones.includes(fila.id) &&
        redondearDinero(descuentoUnitario(fila.producto_id) * fila.cantidad) >
          fila.monto,
    ) ||
    productos.some((item) => {
      const producto = datos?.productos.find((fila) => fila.id === item.producto_id);
      return Boolean(producto && descuentoUnitario(item.producto_id) > producto.precio);
    });
  const pagoPrimario = Number(montoPagoPrimario || total.toFixed(2));
  const pagoSecundario = Number(montoPagoSecundario || 0);
  const pagoExacto =
    Number.isFinite(pagoPrimario) &&
    Number.isFinite(pagoSecundario) &&
    Number(pagoPrimario.toFixed(2)) === pagoPrimario &&
    Number(pagoSecundario.toFixed(2)) === pagoSecundario &&
    Math.round((pagoPrimario + pagoSecundario) * 100) === Math.round(total * 100) &&
    pagoPrimario > 0 &&
    (!metodoPagoSecundarioId || pagoSecundario > 0);
  const puedeEmitir = Boolean(
    clienteId &&
      serieSeleccionada &&
      metodoPagoIdActual &&
    !catalogos.isFetching &&
    total > 0 &&
      pagoExacto &&
      !descuentoExcedePrecio,
  );

  const esComprobante = modo === 'comprobante';
  const esCredito = modo === 'credito';
  const esConsumo = modo === 'consumo';
  const esCalibracion = modo === 'calibracion';
  const transaccionUnica = transacciones.length === 1 ? transacciones[0] : undefined;

  const dniValido = /^\d{8}$/.test(dniConsumo.trim());
  const conductorValido = conductorConsumo.trim().length >= 3;
  const placaConsumoValida = placaConsumo.trim().length > 0 && placaConsumo.trim().length <= 12;
  const kilometrajeNumero = kilometrajeConsumo.trim() === '' ? null : Number(kilometrajeConsumo);
  const kilometrajeValido =
    kilometrajeNumero === null || (Number.isFinite(kilometrajeNumero) && kilometrajeNumero >= 0);
  const observacionesValidas = observacionesConsumo.trim().length <= 500;
  const puedeConsumo = Boolean(
    transaccionUnica !== undefined &&
      dniValido &&
      conductorValido &&
      placaConsumoValida &&
      kilometrajeValido &&
      observacionesValidas,
  );
  const codigoCompleto = /^\d{6}$/.test(codigoCalibracion.trim());
  const codigoValido = validarCodigo.data?.valido === true;
  const puedeCalibracion = Boolean(
    transaccionUnica !== undefined && codigoCompleto && codigoValido,
  );
  const hayResultado =
    resultado !== null || resultadoCredito !== null || resultadoConversion !== null;
  /** Texto del modal de resultado (crédito, consumo interno y calibración). */
  const descripcionResultado =
    resultadoConversion !== null
      ? resultadoConversion.tipo === 'consumo'
        ? 'Consumo interno registrado'
        : 'Calibración registrada'
      : 'Venta a crédito registrada';
  const mensajeResultado =
    resultadoConversion !== null
      ? resultadoConversion.mensaje
      : resultadoCredito
        ? `${resultadoCredito.mensaje} Vale ${resultadoCredito.serie}-${resultadoCredito.credito_venta_id}; total ${formatCurrency(resultadoCredito.total)}.`
        : '';
  const guardando =
    crear.isPending ||
    crearCredito.isPending ||
    registrarConsumo.isPending ||
    registrarCalibracion.isPending;

  useEffect(() => {
    if (!datos || catalogos.isFetching || clienteInicializado.current) return;

    clienteInicializado.current = true;
    const clientesVarios = datos.clientes.find(
      (cliente) =>
        cliente.numero_documento === '00000000' ||
        cliente.nombre.trim().toUpperCase() === 'CLIENTES VARIOS',
    );
    if (clientesVarios) {
      setClienteId(String(clientesVarios.id));
      setClienteSeleccionado(clientesVarios);
    }
  }, [catalogos.isFetching, datos]);

  const cerrar = () => {
    if (!crear.isPending && !crearCredito.isPending) onClose();
  };

  /**
   * Botón del modal de resultado: vuelve a `turno/comprobante` con el formulario
   * limpio para registrar otra operación del mismo tipo, no al turno.
   */
  const volverAlComprobante = () => {
    setResultadoModalAbierto(false);
    if (onVolverAlComprobante) {
      onVolverAlComprobante(modo);
      return;
    }
    onClose();
  };

  const agregarProducto = () => {
    const id = Number(productoId);
    if (!id) return;
    setProductos((actuales) => {
      const existente = actuales.find((item) => item.producto_id === id);
      return existente
        ? actuales.map((item) =>
            item.producto_id === id ? { ...item, cantidad: item.cantidad + 1 } : item,
          )
        : [...actuales, { producto_id: id, cantidad: 1 }];
    });
  };

  const emitir = () => {
    if (!serieSeleccionada) return;
    setErrorPdf('');
    crear.mutate(
      {
        tipo_documento: tipoDocumento,
        serie_id: serieSeleccionada.id,
        aplicar_descuento: aplicarDescuento,
        cliente_id: Number(clienteId),
        transacciones,
        productos,
        metodo_pago_id: Number(metodoPagoIdActual),
        monto_pago_primario: pagoPrimario,
        ...(metodoPagoSecundarioId
          ? { metodo_pago_secundario_id: Number(metodoPagoSecundarioId) }
          : {}),
        monto_pago_secundario: pagoSecundario,
        ...(bancoId ? { banco_id: Number(bancoId) } : {}),
        ...(bancoSecundarioId ? { banco_id_secundario: Number(bancoSecundarioId) } : {}),
        ...(referencia.trim() ? { referencia: referencia.trim() } : {}),
        ...(referenciaSecundaria.trim()
          ? { referencia_secundaria: referenciaSecundaria.trim() }
          : {}),
        monto_pagado: Math.round((pagoPrimario + pagoSecundario) * 100) / 100,
        placa: placa.trim(),
      },
      {
        onSuccess: (respuesta) => {
          setResultado(respuesta);
          setFormatoImpresion('ticket');
          setDialogoFormatoAbierto(true);
        },
      },
    );
  };

  const abrirDocumento = async (formato: 'ticket' | 'a4' = 'ticket') => {
    if (resultadoCredito) {
      try {
        await mostrarPdfAutenticado(turnoApi.ticketCredito(resultadoCredito.credito_venta_id));
        setDialogoFormatoAbierto(false);
        onClose();
      } catch (error) {
        setErrorPdf(error instanceof Error ? error.message : 'No se pudo mostrar el ticket.');
      }
      return;
    }
    if (!resultado) return;
    const ruta =
      resultado.tipo_documento === '99'
        ? turnoApi.ticketNota(resultado.documento_id)
        : turnoApi.ticketDocumento(resultado.venta_id, formato);
    try {
      await mostrarPdfAutenticado(ruta);
      setDialogoFormatoAbierto(false);
      onClose();
    } catch (error) {
      setErrorPdf(error instanceof Error ? error.message : 'No se pudo mostrar el comprobante.');
    }
  };

  const registrarConsumoInterno = () => {
    if (transaccionUnica === undefined || !puedeConsumo) return;

    registrarConsumo.mutate(
      {
        transaccionId: transaccionUnica,
        cuerpo: {
          dni: dniConsumo.trim(),
          conductor: conductorConsumo.trim(),
          placa: placaConsumo.trim().toUpperCase(),
          kilometraje: kilometrajeNumero,
          observaciones: observacionesConsumo.trim(),
        },
      },
      {
        onSuccess: (respuesta) => {
          setResultadoConversion({
            tipo: 'consumo',
            id: respuesta.consumo_id,
            mensaje: `Consumo interno ${respuesta.serie}-${respuesta.numero} registrado.`,
          });
          setResultadoModalAbierto(true);
        },
      },
    );
  };

  const convertirCalibracion = () => {
    if (transaccionUnica === undefined || !puedeCalibracion) return;

    registrarCalibracion.mutate(
      { transaccionId: transaccionUnica, codigo: codigoCalibracion.trim() },
      {
        onSuccess: (respuesta) => {
          setResultadoConversion({
            tipo: 'calibracion',
            id: respuesta.calibration_id,
            mensaje:
              respuesta.nuevo_estado === 'consumido'
                ? `Calibración #${respuesta.calibration_id} registrada. El código quedó consumido.`
                : `Calibración #${respuesta.calibration_id} registrada. Quedan ${respuesta.usos_restantes} usos del código.`,
          });
          setResultadoModalAbierto(true);
        },
      },
    );
  };

  /** Abre el ticket de la conversión recién registrada (mismo formato del panel). */
  const abrirTicketConversion = async () => {
    if (!resultadoConversion) return;

    setErrorPdf('');
    const ruta =
      resultadoConversion.tipo === 'consumo'
        ? turnoApi.ticketConsumoInterno(resultadoConversion.id)
        : turnoApi.ticketCalibracion(resultadoConversion.id);

    try {
      await mostrarPdfAutenticado(ruta);
    } catch (error) {
      setErrorPdf(error instanceof Error ? error.message : 'No se pudo abrir el ticket.');
    }
  };

  /** Abre el ticket del resultado vigente (vale o conversión). */
  const abrirTicketResultado = async () => {
    if (resultadoConversion) {
      await abrirTicketConversion();
      return;
    }
    await abrirDocumento('ticket');
  };

  /** Completa el nombre del conductor desde RENIEC a partir del DNI. */
  const consultarDniConductor = (numero: string, asignarNombre: (nombre: string) => void) => {
    if (!/^\d{8}$/.test(numero)) return;

    consultarDni.reset();
    consultarDni.mutate(numero, {
      onSuccess: (respuesta) => {
        const nombre = respuesta.documento.nombre ?? '';
        if (nombre !== '') asignarNombre(nombre);
      },
    });
  };

  const ubicarLineaCredito = () => {
    const ruc = busquedaRucCredito.trim();
    const placaBuscada = busquedaPlacaCredito.trim();
    if (ruc === '' && placaBuscada === '') return;

    setCreditoId('');
    buscarLinea.mutate(ruc !== '' ? { ruc } : { placa: placaBuscada }, {
      onSuccess: (respuesta) => {
        if (respuesta.criterio === 'placa') setPlaca(placaBuscada.toUpperCase());
      },
    });
  };

  const venderCredito = () => {
    const transaccionId = transacciones[0];
    if (transaccionId === undefined || !lineaCompatible) return;

    setErrorPdf('');
    crearCredito.mutate(
      {
        credito_id: Number(creditoId),
        transaccion_id: transaccionId,
        placa: placa.trim(),
        ...(conductorDniCredito.trim() ? { conductor_dni: conductorDniCredito.trim() } : {}),
        ...(conductorNombreCredito.trim()
          ? { conductor_nombre: conductorNombreCredito.trim() }
          : {}),
        ...(kilometrajeNumeroCredito !== null && Number.isFinite(kilometrajeNumeroCredito)
          ? { kilometraje: kilometrajeNumeroCredito }
          : {}),
        ...(valeCredito.trim() ? { vale: valeCredito.trim() } : {}),
      },
      {
        onSuccess: (respuesta) => {
          setResultadoCredito(respuesta);
          setResultadoModalAbierto(true);
        },
      },
    );
  };

  const guardarDireccion = () => {
    const id = Number(clienteId);
    if (!Number.isInteger(id) || id <= 0) return;

    actualizarDireccion.mutate(
      { clienteId: id, direccion: direccionEditada.trim() },
      {
        onSuccess: (respuesta) => {
          setClienteSeleccionado((actual) =>
            actual?.id === id ? { ...actual, direccion: respuesta.direccion } : actual,
          );
          setDialogoDireccionAbierto(false);
        },
      },
    );
  };

  const titulo = resultadoCredito
    ? 'Venta a crédito registrada'
    : resultado
      ? 'Comprobante creado'
      : resultadoConversion !== null
        ? 'Operación registrada'
        : modo === 'credito'
          ? 'Registrar venta a crédito'
          : modo === 'consumo'
            ? 'Registrar consumo interno'
            : modo === 'calibracion'
              ? 'Convertir a calibración'
              : 'Crear comprobante';
  const descripcion = resultadoCredito
    ? 'El vale quedó registrado en tu turno y en la línea de crédito.'
    : resultado
      ? 'El documento quedó registrado en el sistema.'
      : modo === 'credito'
        ? 'Selecciona la línea de crédito y una transacción de tus surtidores asignados.'
        : modo === 'consumo'
          ? 'Elige la transacción del surtidor y registra los datos del conductor y del vehículo.'
          : modo === 'calibracion'
            ? 'Elige la transacción del surtidor y el código de calibración validado.'
            : 'Selecciona el cliente, las ventas del surtidor o productos y el método de pago.';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Mi turno</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink-900">{titulo}</h1>
          <p className="mt-1 max-w-3xl text-sm text-ink-500">{descripcion}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hayResultado ? (
            <>
              <Button variant="outline" onClick={cerrar}>
                Volver al turno
              </Button>
              {resultado ? (
                <Button
                  leftIcon={<Printer className="size-4" aria-hidden="true" />}
                  onClick={() => {
                    setErrorPdf('');
                    setDialogoFormatoAbierto(true);
                  }}
                >
                  Elegir formato de impresión
                </Button>
              ) : resultadoCredito ? (
                <Button
                  leftIcon={<Printer className="size-4" aria-hidden="true" />}
                  onClick={() => void abrirDocumento('ticket')}
                >
                  Abrir ticket
                </Button>
              ) : resultadoConversion ? (
                <Button
                  leftIcon={<Printer className="size-4" aria-hidden="true" />}
                  onClick={() => void abrirTicketConversion()}
                >
                  Abrir ticket
                </Button>
              ) : null}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={cerrar} disabled={guardando}>
                Volver al turno
              </Button>
              {esCredito ? (
                <Button
                  leftIcon={<Receipt className="size-4" aria-hidden="true" />}
                  loading={crearCredito.isPending}
                  disabled={
                    !datos?.turno_id || !creditoId || transacciones.length !== 1 || !lineaCompatible
                  }
                  onClick={venderCredito}
                >
                  Registrar venta a crédito
                </Button>
              ) : esConsumo ? (
                <Button
                  leftIcon={<Fuel className="size-4" aria-hidden="true" />}
                  loading={registrarConsumo.isPending}
                  disabled={!datos?.turno_id || !puedeConsumo}
                  onClick={registrarConsumoInterno}
                >
                  Registrar consumo interno
                </Button>
              ) : esCalibracion ? (
                <Button
                  leftIcon={<Gauge className="size-4" aria-hidden="true" />}
                  loading={registrarCalibracion.isPending}
                  disabled={!datos?.turno_id || !puedeCalibracion}
                  onClick={convertirCalibracion}
                >
                  Convertir a calibración
                </Button>
              ) : (
                <Button
                  leftIcon={<Receipt className="size-4" aria-hidden="true" />}
                  loading={crear.isPending}
                  disabled={!datos?.turno_id || !puedeEmitir}
                  onClick={emitir}
                >
                  Emitir {formatCurrency(total)}
                </Button>
              )}
            </>
          )}
        </div>
      </header>

      {resultadoCredito || resultadoConversion ? null : (
      <Card>
        <CardContent>
        {hayResultado ? (
          <div className="space-y-4">
            <Alert variant="success" title="Listo">
              {`${resultado?.mensaje} Se generó ${resultado?.serie}-${resultado?.numero} por ${formatCurrency(resultado?.total ?? 0)}.${resultado?.tipo_documento !== '99' ? ' El comprobante quedó pendiente de procesamiento.' : ''}`}
            </Alert>
            {descuentoExcedePrecio ? (
              <Alert variant="error" title="Descuento mayor al importe de la venta">
                Revisa la configuración del descuento del cliente o la placa. No se puede aplicar un descuento superior al valor del producto.
              </Alert>
            ) : null}
          </div>
        ) : (
          <div className="space-y-5">
          {catalogos.isError ? (
            <Alert variant="error" title="No se pudieron cargar los datos">
              {mensajeError(catalogos.error)}
            </Alert>
          ) : null}
          {!datos?.turno_id && !catalogos.isLoading ? (
            <Alert variant="error" title="No hay turno abierto">
              Abre tu turno antes de crear un comprobante.
            </Alert>
          ) : null}
          {modo === 'comprobante' &&
          !catalogos.isLoading &&
          seriesDisponibles.length === 0 ? (
            <Alert variant="error" title="No hay series de punto de venta disponibles">
              Configura y activa una serie para este tipo de documento en el catálogo de series.
            </Alert>
          ) : null}
          {crear.isError ? (
            <Alert variant="error" title="No se pudo emitir">
              {mensajeError(crear.error)}
            </Alert>
          ) : null}
          {crearCredito.isError ? (
            <Alert variant="error" title="No se pudo registrar la venta a crédito">
              {mensajeError(crearCredito.error)}
            </Alert>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button
              variant={modo === 'comprobante' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setModo('comprobante')}
            >
              Comprobante
            </Button>
            <Button
              variant={modo === 'credito' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setModo('credito')}
            >
              Venta a crédito
            </Button>
            <Button
              variant={modo === 'consumo' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setModo('consumo')}
            >
              Consumo interno
            </Button>
            <Button
              variant={modo === 'calibracion' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setModo('calibracion')}
            >
              Calibración
            </Button>
          </div>

            {esComprobante ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {esComprobante ? (
                <label className="text-sm text-ink-600">
                  Tipo de comprobante
                  <Select
                    className="mt-1 h-11 w-full"
                    value={tipoDocumento}
                    onChange={(evento) => {
                      const tipo = evento.target.value as '01' | '03' | '99';
                      if (
                        tipo === '01' &&
                        clienteActual &&
                        (clienteActual.tipo_documento !== '6' ||
                          !/^\d{11}$/.test(clienteActual.numero_documento ?? ''))
                      ) {
                        setClienteId('');
                        setClienteSeleccionado(null);
                      }
                      setTipoDocumento(tipo);
                      if (tipo === '99') {
                        setMostrarMetodoSecundario(false);
                        setMetodoPagoSecundarioId('');
                        setMontoPagoSecundario('');
                        setBancoSecundarioId('');
                        setReferenciaSecundaria('');
                      }
                    }}
                  >
                    <option value="03">Boleta</option>
                    <option value="01">Factura</option>
                    <option value="99">Nota de venta</option>
                  </Select>
                </label>
              ) : null}
            {modo === 'comprobante' ? (
              <label className="text-sm text-ink-600">
                Serie de punto de venta
                <Select
                  className="mt-1 h-11 w-full"
                  value={serieSeleccionada ? String(serieSeleccionada.id) : ''}
                  disabled={catalogos.isLoading || seriesDisponibles.length === 0}
                  onChange={(evento) => setSerieId(evento.target.value)}
                >
                  {seriesDisponibles.length === 0 ? (
                    <option value="">No hay series activas para este documento</option>
                  ) : null}
                  {seriesDisponibles.map((serie) => (
                    <option key={serie.id} value={serie.id}>{serie.serie}</option>
                  ))}
                </Select>
                <span className="mt-1 block text-xs text-ink-400">
                  Serie configurada para emitir en punto de venta.
                </span>
              </label>
            ) : null}
            {esComprobante ? (
            <div className="text-sm text-ink-600">
              <label>
                Cliente
                <Select
                  className="mt-1 h-11 w-full"
                  value={clienteId}
                  onChange={(evento) => {
                    const id = evento.target.value;
                    setClienteId(id);
                    setClienteSeleccionado(
                      datos?.clientes.find((cliente) => String(cliente.id) === id) ??
                        (datos?.cliente_seleccionado?.id === Number(id)
                          ? datos.cliente_seleccionado
                          : null),
                    );
                    setCreditoId('');
                  }}
                  disabled={catalogos.isLoading}
                  searchable
                  onSearchChange={setTextoBusquedaCliente}
                >
                  <option value="">Seleccionar cliente</option>
                  {opcionesClientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombre} · {cliente.numero_documento}
                    </option>
                  ))}
                </Select>
              </label>
              {clienteId ? (
                <div className="mt-1 flex min-h-7 items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-xs text-ink-500">
                    {clienteActual?.direccion
                      ? `Dirección: ${clienteActual.direccion}`
                      : catalogos.isFetching
                        ? 'Cargando dirección del cliente…'
                        : 'Dirección no registrada.'}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 shrink-0 px-2 text-xs"
                    leftIcon={<Pencil className="size-3" aria-hidden="true" />}
                    onClick={() => {
                      setDireccionEditada(clienteActual?.direccion ?? '');
                      actualizarDireccion.reset();
                      setDialogoDireccionAbierto(true);
                    }}
                  >
                    Editar dirección
                  </Button>
                </div>
              ) : null}
            </div>
            ) : null}
            <label className="text-sm text-ink-600">
              Placa (opcional)
              <Input
                className="mt-1"
                value={placa}
                maxLength={12}
                onChange={(evento) => setPlaca(evento.target.value.toUpperCase())}
              />
            </label>
            </div>
            ) : null}
          {modo === 'comprobante' ? (
            <label className="flex cursor-pointer items-start gap-3 rounded-control border border-ink-200 bg-ink-50/70 p-3 text-sm text-ink-700">
              <input
                type="checkbox"
                className="mt-0.5 size-4 accent-brand-600"
                checked={aplicarDescuento}
                onChange={(evento) => setAplicarDescuento(evento.target.checked)}
              />
              <span>
                <span className="block font-semibold text-ink-800">Aplicar descuentos del cliente y la placa</span>
                <span className="mt-0.5 block text-xs text-ink-500">
                  {datos?.descuentos.length
                    ? `Se encontraron ${datos.descuentos.length} descuento(s) configurado(s). El total se actualizará al aplicarlos.`
                    : 'Sólo se aplican descuentos configurados para este cliente, placa y producto.'}
                </span>
              </span>
            </label>
          ) : null}

          {esCredito ? (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-ink-800">Ubicar la línea de crédito</h3>
                <p className="text-xs text-ink-500">
                  Por seguridad, la línea se ubica con la placa del vehículo o el RUC del cliente; no
                  se listan todos los clientes.
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <Input
                  containerClassName="min-w-40 flex-1"
                  label="Placa del vehículo"
                  value={busquedaPlacaCredito}
                  maxLength={12}
                  placeholder="ABC-123"
                  onChange={(evento) => setBusquedaPlacaCredito(evento.target.value.toUpperCase())}
                />
                <Input
                  containerClassName="min-w-40 flex-1"
                  label="RUC del cliente"
                  value={busquedaRucCredito}
                  maxLength={11}
                  inputMode="numeric"
                  placeholder="11 dígitos"
                  onChange={(evento) =>
                    setBusquedaRucCredito(evento.target.value.replace(/\D/g, ''))
                  }
                />
                <Button
                  type="button"
                  variant="outline"
                  leftIcon={<Search className="size-4" aria-hidden="true" />}
                  loading={buscarLinea.isPending}
                  disabled={busquedaPlacaCredito.trim() === '' && busquedaRucCredito.trim() === ''}
                  onClick={ubicarLineaCredito}
                >
                  Buscar línea
                </Button>
              </div>

              {buscarLinea.isError ? (
                <Alert variant="error" title="No se pudo ubicar la línea">
                  {mensajeError(buscarLinea.error)}
                </Alert>
              ) : null}

              {clienteCredito ? (
                <label className="block text-sm text-ink-600">
                  Línea de crédito
                  <Select
                    className="mt-1 h-11 w-full"
                    value={creditoId}
                    onChange={(evento) => setCreditoId(evento.target.value)}
                    searchable
                  >
                    <option value="">Seleccionar línea</option>
                    {lineasCredito.map((credito) => (
                      <option key={credito.id} value={credito.id}>
                        {credito.producto} ·{' '}
                        {credito.unidad === 'galones'
                          ? `${credito.disponible.toFixed(3)} gal`
                          : formatCurrency(credito.disponible)}
                        {credito.flexibilidad > 0
                          ? ` (+ ${credito.unidad === 'galones' ? `${credito.flexibilidad.toFixed(3)} gal` : formatCurrency(credito.flexibilidad)} de flexibilidad)`
                          : ''}
                      </option>
                    ))}
                  </Select>
                  <span className="mt-1 block text-xs text-ink-400">
                    {clienteCredito.nombre} · {clienteCredito.numero_documento}
                  </span>
                </label>
              ) : null}

              {clienteCredito && lineasCredito.length === 0 ? (
                <Alert variant="warning" title="El cliente no tiene líneas disponibles">
                  No hay líneas activas pendientes para este cliente. Las que exigen factura por
                  despacho todavía no están habilitadas en el POS web.
                </Alert>
              ) : null}

              {lineaCredito && creditoTransaccion ? (
                <div className="rounded-control border border-ink-100 bg-ink-50/60 p-3">
                  <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                    <div>
                      <p className="text-xs text-ink-500">Cliente</p>
                      <p
                        className="truncate font-medium text-ink-800"
                        title={lineaCredito.cliente_nombre}
                      >
                        {lineaCredito.cliente_nombre}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-ink-500">Precio</p>
                      <p className="tabular-nums font-medium text-ink-800">
                        {formatCurrency(creditoTransaccion.precio)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-ink-500">
                        {descuentoLineaUnitario < 0 ? 'Recargo de la línea' : 'Descuento de la línea'}
                      </p>
                      <p className="tabular-nums font-medium text-ink-800">
                        {descuentoLineaUnitario !== 0
                          ? `${formatCurrency(Math.abs(descuentoLineaUnitario))} por ${lineaCredito.unidad === 'galones' ? 'galón' : 'unidad'}`
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-ink-500">Total del vale</p>
                      <p className="tabular-nums font-semibold text-ink-900">
                        {formatCurrency(totalCredito)}
                      </p>
                    </div>
                  </div>
                  {descuentoLineaUnitario !== 0 ? (
                    <p
                      className={
                        descuentoLineaUnitario > 0
                          ? 'mt-2 text-xs text-emerald-700'
                          : 'mt-2 text-xs text-amber-700'
                      }
                    >
                      {descuentoLineaUnitario > 0
                        ? 'Se aplicará un descuento de '
                        : 'Se aplicará un recargo de '}
                      {formatCurrency(
                        Math.abs(
                          redondearDinero(descuentoLineaUnitario * creditoTransaccion.cantidad),
                        ),
                      )}{' '}
                      sobre la transacción.
                    </p>
                  ) : null}
                  {!lineaCompatible ? (
                    <p className="mt-2 text-xs text-rose-700">
                      Esta línea es sólo para {lineaCredito.producto}; la transacción seleccionada es
                      de otro producto.
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="flex items-end gap-2">
                  <Input
                    containerClassName="flex-1"
                    label="DNI del conductor"
                    value={conductorDniCredito}
                    maxLength={8}
                    inputMode="numeric"
                    placeholder="8 dígitos"
                    onChange={(evento) =>
                      setConductorDniCredito(evento.target.value.replace(/\D/g, ''))
                    }
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-11 shrink-0"
                    title="Consultar DNI en RENIEC"
                    aria-label="Consultar DNI en RENIEC"
                    leftIcon={<Search className="size-4" aria-hidden="true" />}
                    loading={consultarDni.isPending}
                    disabled={!/^\d{8}$/.test(conductorDniCredito.trim())}
                    onClick={() =>
                      consultarDniConductor(conductorDniCredito.trim(), setConductorNombreCredito)
                    }
                  />
                </div>
                <Input
                  label="Nombre del conductor"
                  value={conductorNombreCredito}
                  maxLength={150}
                  placeholder="Nombres y apellidos"
                  onChange={(evento) => setConductorNombreCredito(evento.target.value)}
                />
                <Input
                  label="Kilometraje (opcional)"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={kilometrajeCredito}
                  onChange={(evento) => setKilometrajeCredito(evento.target.value)}
                />
                <Input
                  label="Vale (opcional)"
                  value={valeCredito}
                  maxLength={50}
                  placeholder="N.º de vale"
                  onChange={(evento) => setValeCredito(evento.target.value)}
                />
              </div>
              {consultarDni.isError ? (
                <Alert variant="error" title="No se pudo consultar el DNI">
                  {mensajeError(consultarDni.error)}
                </Alert>
              ) : null}
            </div>
          ) : null}

          <div className={modo === 'comprobante' ? 'grid items-start gap-6 lg:grid-cols-2' : ''}>
          <section className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-ink-800">
                  {modo === 'comprobante'
                    ? 'Transacciones del surtidor'
                    : 'Transacción del surtidor (selecciona una)'}
                </h3>
                <p className="text-xs text-ink-500">
                  Sólo se muestran transacciones pendientes de tus mangueras y de este turno.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<RefreshCw className="size-3.5" aria-hidden="true" />}
                loading={catalogos.isFetching}
                onClick={() => void catalogos.refetch()}
              >
                Actualizar listado
              </Button>
            </div>
            {(datos?.transacciones.length ?? 0) === 0 ? (
              <p className="rounded-control border border-dashed border-ink-200 p-3 text-sm text-ink-500">
                No hay transacciones pendientes disponibles para este surtidor y turno.
              </p>
            ) : (
              <div className="max-h-52 overflow-auto rounded-control border border-ink-100">
                {(datos?.transacciones ?? []).map((fila) => (
                  <div
                    key={fila.id}
                    className="flex flex-wrap items-center gap-3 border-b border-ink-100 px-3 py-2.5 last:border-0 hover:bg-ink-50"
                  >
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        checked={transacciones.includes(fila.id)}
                        onChange={(evento) =>
                          setTransacciones((seleccionadas) => {
                            if (modo !== 'comprobante') {
                              return evento.target.checked ? [fila.id] : [];
                            }
                            return evento.target.checked
                              ? [...seleccionadas, fila.id]
                              : seleccionadas.filter((id) => id !== fila.id);
                          })
                        }
                      />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-ink-800">
                        {fila.producto} · Bomba {fila.bomba}, manguera {fila.manguera}
                      </span>
                      <span className="text-xs text-ink-500">
                        {formatDateTimeValue(fila.fecha)} · {fila.cantidad} ·{' '}
                        {formatCurrency(
                          redondearDinero(
                            fila.monto -
                              redondearDinero(
                                descuentoUnitario(fila.producto_id) * fila.cantidad,
                              ),
                          ),
                        )}
                        {aplicarDescuento && descuentoUnitario(fila.producto_id) > 0
                          ? ` · Descuento cliente ${formatCurrency(
                              redondearDinero(
                                descuentoUnitario(fila.producto_id) * fila.cantidad,
                              ),
                            )}`
                          : ''}
                      </span>
                    </span>
                    <span className="text-xs text-ink-400">#{fila.id}</span>
                    </label>
                  </div>
                ))}
              </div>
            )}
          </section>

            {esConsumo ? (
              <section className="space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-ink-800">
                    Datos del conductor y del vehículo
                  </h3>
                  <p className="text-xs text-ink-500">
                    El consumo interno descuenta la transacción del turno sin generar efectivo.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex items-end gap-2">
                    <Input
                      containerClassName="flex-1"
                      label="DNI del conductor"
                      value={dniConsumo}
                      maxLength={8}
                      inputMode="numeric"
                      placeholder="8 dígitos"
                      error={
                        dniConsumo !== '' && !dniValido ? 'El DNI debe tener 8 dígitos.' : undefined
                      }
                      onChange={(evento) => setDniConsumo(evento.target.value.replace(/\D/g, ''))}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="size-11 shrink-0"
                      title="Consultar DNI en RENIEC"
                      aria-label="Consultar DNI en RENIEC"
                      leftIcon={<Search className="size-4" aria-hidden="true" />}
                      loading={consultarDni.isPending}
                      disabled={!dniValido}
                      onClick={() => consultarDniConductor(dniConsumo.trim(), setConductorConsumo)}
                    />
                  </div>
                  <Input
                    label="Nombre del conductor"
                    value={conductorConsumo}
                    maxLength={150}
                    placeholder="Nombres y apellidos"
                    error={
                      conductorConsumo !== '' && !conductorValido
                        ? 'Ingresa al menos 3 caracteres.'
                        : undefined
                    }
                    onChange={(evento) => setConductorConsumo(evento.target.value)}
                  />
                  <Input
                    label="Placa del vehículo"
                    value={placaConsumo}
                    maxLength={12}
                    placeholder="ABC-123"
                    onChange={(evento) => setPlacaConsumo(evento.target.value.toUpperCase())}
                  />
                  <Input
                    label="Kilometraje (opcional)"
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={kilometrajeConsumo}
                    hint="En kilómetros."
                    error={
                      !kilometrajeValido ? 'El kilometraje debe ser mayor o igual a cero.' : undefined
                    }
                    onChange={(evento) => setKilometrajeConsumo(evento.target.value)}
                  />
                </div>
                {consultarDni.isError ? (
                  <Alert variant="error" title="No se pudo consultar el DNI">
                    {mensajeError(consultarDni.error)}
                  </Alert>
                ) : null}
                <label className="block text-sm font-medium text-ink-700">
                  Observaciones (opcional)
                  <textarea
                    className="mt-1 min-h-20 w-full rounded-control border border-ink-200 bg-white px-3 py-2 text-sm text-ink-800 shadow-sm outline-none transition-colors focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
                    maxLength={500}
                    value={observacionesConsumo}
                    placeholder="Observaciones del consumo interno…"
                    onChange={(evento) => setObservacionesConsumo(evento.target.value)}
                  />
                  <span className="mt-1 block text-right text-xs text-ink-400">
                    {observacionesConsumo.trim().length}/500
                  </span>
                </label>
                {registrarConsumo.isError ? (
                  <Alert variant="error" title="No se pudo registrar el consumo interno">
                    {mensajeError(registrarConsumo.error)}
                  </Alert>
                ) : null}
              </section>
            ) : null}

            {esCalibracion ? (
              <section className="space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-ink-800">Código de calibración</h3>
                  <p className="text-xs text-ink-500">
                    Valida un código activo de 6 dígitos y conviértelo con la transacción
                    seleccionada.
                  </p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <Input
                    containerClassName="min-w-40 flex-1"
                    label="Código"
                    value={codigoCalibracion}
                    maxLength={6}
                    inputMode="numeric"
                    placeholder="6 dígitos"
                    onChange={(evento) => {
                      validarCodigo.reset();
                      setCodigoCalibracion(evento.target.value.replace(/\D/g, ''));
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    loading={validarCodigo.isPending}
                    disabled={!codigoCompleto}
                    onClick={() => {
                      const valor = codigoCalibracion.trim();
                      if (/^\d{6}$/.test(valor)) validarCodigo.mutate(valor);
                    }}
                  >
                    Validar
                  </Button>
                </div>
                {validarCodigo.isError ? (
                  <Alert variant="error" title="Código no encontrado">
                    {mensajeError(validarCodigo.error)}
                  </Alert>
                ) : null}
                {validarCodigo.data ? (
                  validarCodigo.data.valido ? (
                    <Alert variant="success" title="Código válido y activo">
                      Usos restantes: {validarCodigo.data.codigo.usos_restantes}/
                      {validarCodigo.data.codigo.cantidad_usos}.
                    </Alert>
                  ) : (
                    <Alert variant="warning" title="Código no disponible">
                      Estado: {validarCodigo.data.codigo.estado_efectivo}. Usos restantes:{' '}
                      {validarCodigo.data.codigo.usos_restantes}.
                    </Alert>
                  )
                ) : null}
                {registrarCalibracion.isError ? (
                  <Alert variant="error" title="No se pudo registrar la calibración">
                    {mensajeError(registrarCalibracion.error)}
                  </Alert>
                ) : null}
              </section>
            ) : null}

            {modo === 'comprobante' ? (
              <section className="space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-ink-800">Productos adicionales</h3>
                  <p className="text-xs text-ink-500">
                    El precio se toma del catálogo y el stock de lubricantes se valida al emitir.
                  </p>
                </div>
            <div className="flex flex-wrap gap-2">
              <Select
                className="h-11 min-w-56 flex-1"
                value={productoId}
                onChange={(evento) => setProductoId(evento.target.value)}
                searchable
              >
                <option value="">Seleccionar producto</option>
                {(datos?.productos ?? []).map((producto) => (
                  <option key={producto.id} value={producto.id}>
                    {producto.nombre} ·{' '}
                    {formatCurrency(precioConDescuento(producto.id, producto.precio))}
                  </option>
                ))}
              </Select>
              <Button
                variant="outline"
                leftIcon={<Plus className="size-4" aria-hidden="true" />}
                disabled={!productoId}
                onClick={agregarProducto}
              >
                Agregar
              </Button>
            </div>
            {productos.map((item) => {
              const producto = datos?.productos.find((fila) => fila.id === item.producto_id);
              if (!producto) return null;
              return (
                <div
                  key={producto.id}
                  className="flex flex-wrap items-center gap-3 rounded-control border border-ink-100 p-3"
                >
                  <span className="min-w-40 flex-1 text-sm font-medium text-ink-700">
                    {producto.nombre} ·{' '}
                    {formatCurrency(precioConDescuento(producto.id, producto.precio))}
                    {descuentoUnitario(producto.id) > 0 ? (
                      <span className="ml-1 text-xs font-normal text-emerald-700">
                        (desc. {formatCurrency(descuentoUnitario(producto.id))} por unidad)
                      </span>
                    ) : null}
                  </span>
                  <label className="text-xs text-ink-500">
                    Cantidad
                    <Input
                      type="number"
                      min="0.001"
                      step="0.001"
                      className="mt-1 w-28"
                      value={item.cantidad}
                      onChange={(evento) => {
                        const cantidad = Number(evento.target.value);
                        setProductos((actuales) =>
                          actuales.map((fila) =>
                            fila.producto_id === producto.id
                              ? { ...fila, cantidad: Number.isFinite(cantidad) ? cantidad : 0 }
                              : fila,
                          ),
                        );
                      }}
                    />
                  </label>
                  <span className="w-24 text-right text-sm font-semibold text-ink-800">
                    {formatCurrency(
                      redondearDinero(
                        precioConDescuento(producto.id, producto.precio) * item.cantidad,
                      ),
                    )}
                  </span>
                  <button
                    type="button"
                    aria-label={`Quitar ${producto.nombre}`}
                    className="rounded-control p-2 text-rose-600 hover:bg-rose-50"
                    onClick={() =>
                      setProductos((actuales) =>
                        actuales.filter((fila) => fila.producto_id !== producto.id),
                      )
                    }
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
              </section>
            ) : null}
          </div>

            {modo === 'comprobante' ? (
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <label className="text-sm text-ink-600">
                    Método de pago primario
                    <Select
                      className="mt-1 h-11 w-full"
                      value={metodoPagoIdActual}
                      onChange={(evento) => {
                        const id = evento.target.value;
                        setMetodoPagoId(id);
                        if (id === metodoPagoSecundarioId) {
                          setMetodoPagoSecundarioId('');
                          setMontoPagoSecundario('');
                          setBancoSecundarioId('');
                          setReferenciaSecundaria('');
                        }
                      }}
                    >
                      {!metodoContado ? <option value="">Seleccionar método</option> : null}
                      {(datos?.metodos_pago ?? []).map((metodo) => (
                        <option key={metodo.id} value={metodo.id}>{metodo.nombre}</option>
                      ))}
                    </Select>
                  </label>
                  <label className="text-sm text-ink-600">
                    Monto primario
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      className="mt-1"
                      value={montoPagoPrimario || total.toFixed(2)}
                      onChange={(evento) => setMontoPagoPrimario(evento.target.value)}
                    />
                  </label>
                  <label className="text-sm text-ink-600">
                    Banco primario (opcional)
                    <Select
                      className="mt-1 h-11 w-full"
                      value={bancoId}
                      onChange={(evento) => setBancoId(evento.target.value)}
                    >
                      <option value="">Sin banco</option>
                      {(datos?.bancos ?? []).map((banco) => (
                        <option key={banco.id} value={banco.id}>{banco.nombre}</option>
                      ))}
                    </Select>
                  </label>
                  <label className="text-sm text-ink-600">
                    Referencia primaria (opcional)
                    <Input
                      className="mt-1"
                      value={referencia}
                      maxLength={100}
                      onChange={(evento) => setReferencia(evento.target.value)}
                      placeholder="N.º de operación"
                    />
                  </label>
                </div>
                {tipoDocumento !== '99' && mostrarMetodoSecundario ? (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <label className="text-sm text-ink-600">
                      Método de pago secundario (opcional)
                      <Select
                        className="mt-1 h-11 w-full"
                        value={metodoPagoSecundarioId}
                        onChange={(evento) => {
                          const id = evento.target.value;
                          setMetodoPagoSecundarioId(id);
                          setMontoPagoSecundario('');
                          if (id && !montoPagoPrimario) {
                            setMontoPagoPrimario(total.toFixed(2));
                          }
                          if (!id) {
                            setMontoPagoSecundario('');
                            setBancoSecundarioId('');
                            setReferenciaSecundaria('');
                          }
                        }}
                      >
                        <option value="">Sin segundo método</option>
                        {(datos?.metodos_pago ?? []).map((metodo) => (
                          <option
                            key={metodo.id}
                            value={metodo.id}
                            disabled={String(metodo.id) === metodoPagoIdActual}
                          >
                            {metodo.nombre}
                          </option>
                        ))}
                      </Select>
                    </label>
                    <label className="text-sm text-ink-600">
                      Monto secundario
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        className="mt-1"
                        value={montoPagoSecundario}
                        disabled={!metodoPagoSecundarioId}
                        onChange={(evento) => setMontoPagoSecundario(evento.target.value)}
                      />
                    </label>
                    <label className="text-sm text-ink-600">
                      Banco secundario (opcional)
                      <Select
                        className="mt-1 h-11 w-full"
                        value={bancoSecundarioId}
                        disabled={!metodoPagoSecundarioId}
                        onChange={(evento) => setBancoSecundarioId(evento.target.value)}
                      >
                        <option value="">Sin banco</option>
                        {(datos?.bancos ?? []).map((banco) => (
                          <option key={banco.id} value={banco.id}>{banco.nombre}</option>
                        ))}
                      </Select>
                    </label>
                    <label className="text-sm text-ink-600">
                      Referencia secundaria (opcional)
                      <Input
                        className="mt-1"
                        value={referenciaSecundaria}
                        maxLength={100}
                        disabled={!metodoPagoSecundarioId}
                        onChange={(evento) => setReferenciaSecundaria(evento.target.value)}
                        placeholder="N.º de operación"
                      />
                    </label>
                  </div>
                ) : null}
                {tipoDocumento !== '99' ? (
                  <div className="flex flex-wrap gap-2">
                    {!mostrarMetodoSecundario ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
                        onClick={() => {
                          setMontoPagoPrimario(total.toFixed(2));
                          setMostrarMetodoSecundario(true);
                        }}
                      >
                        Agregar método de pago
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        leftIcon={<Trash2 className="size-3.5" aria-hidden="true" />}
                        onClick={() => {
                          setMostrarMetodoSecundario(false);
                          setMetodoPagoSecundarioId('');
                          setMontoPagoSecundario('');
                          setBancoSecundarioId('');
                          setReferenciaSecundaria('');
                          setMontoPagoPrimario(total.toFixed(2));
                        }}
                      >
                        Quitar segundo método
                      </Button>
                    )}
                  </div>
                ) : null}
                {!pagoExacto ? (
                  <p className="text-xs font-medium text-rose-700" role="status">
                    Los montos deben ser positivos y sumar exactamente {formatCurrency(total)}. No se acepta vuelto.
                  </p>
                ) : null}
              </div>
            ) : null}

          {modo === 'comprobante' ? (
            <div className="flex items-center justify-between rounded-control bg-ink-50 px-4 py-3">
              <span className="text-sm font-semibold text-ink-700">Total del comprobante</span>
              <span className="text-lg font-bold tabular-nums text-ink-900">
                {formatCurrency(total)}
              </span>
            </div>
          ) : null}
          </div>
        )}
        </CardContent>
      </Card>
      )}
      {errorPdf && (resultadoCredito || resultadoConversion) ? (
        <Alert variant="error" title="No se pudo imprimir">{errorPdf}</Alert>
      ) : null}
      {resultado ? (
        <Dialog
          open={dialogoFormatoAbierto}
          onClose={() => setDialogoFormatoAbierto(false)}
          title="Comprobante creado"
          description="Confirma el número generado y elige el formato que deseas imprimir."
          size="md"
          footer={
            <>
              <Button variant="outline" onClick={() => setDialogoFormatoAbierto(false)}>
                Ahora no
              </Button>
              <Button
                leftIcon={<Printer className="size-4" aria-hidden="true" />}
                onClick={() => void abrirDocumento(formatoImpresion)}
              >
                Abrir {formatoImpresion === 'a4' ? 'comprobante A4' : 'ticket 80 mm'}
              </Button>
            </>
          }
        >
          <div className="space-y-5">
            <div className="flex items-start gap-3 rounded-control border border-emerald-200 bg-emerald-50 p-4">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <CheckCircle2 className="size-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-emerald-900">Emisión registrada correctamente</p>
                <p className="mt-1 text-xs leading-relaxed text-emerald-800">{resultado.mensaje}</p>
              </div>
            </div>

            <div className="rounded-card border border-ink-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                    {resultado.tipo_documento === '01'
                      ? 'Factura'
                      : resultado.tipo_documento === '03'
                        ? 'Boleta'
                        : 'Nota de venta'}
                  </p>
                  <p className="mt-1 font-mono text-xl font-bold tracking-wide text-ink-900">
                    {resultado.serie}-{resultado.numero}
                  </p>
                  <p className="mt-1 text-xs text-ink-500">
                    Serie <span className="font-semibold text-ink-700">{resultado.serie}</span>
                    <span className="mx-2 text-ink-300">·</span>
                    Número <span className="font-semibold text-ink-700">{resultado.numero}</span>
                  </p>
                </div>
                <div className="rounded-control bg-ink-50 px-3 py-2 text-right">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-ink-400">Total</p>
                  <p className="mt-0.5 text-lg font-bold tabular-nums text-ink-900">
                    {formatCurrency(resultado.total)}
                  </p>
                </div>
              </div>
            </div>

            <fieldset>
              <legend className="mb-2 text-sm font-semibold text-ink-800">
                ¿Qué formato deseas imprimir?
              </legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  aria-pressed={formatoImpresion === 'ticket'}
                  onClick={() => setFormatoImpresion('ticket')}
                  className={`rounded-control border p-4 text-left transition-colors ${
                    formatoImpresion === 'ticket'
                      ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-200'
                      : 'border-ink-200 bg-white hover:border-brand-300 hover:bg-brand-50/50'
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                      <Receipt className="size-4 text-brand-600" aria-hidden="true" />
                      Ticket 80 mm
                    </span>
                    <span
                      className={`size-4 rounded-full border-2 ${
                        formatoImpresion === 'ticket'
                          ? 'border-brand-600 bg-brand-600 ring-2 ring-white'
                          : 'border-ink-300'
                      }`}
                      aria-hidden="true"
                    />
                  </span>
                  <span className="mt-2 block text-xs leading-relaxed text-ink-500">
                    Ideal para impresora térmica de tickets.
                  </span>
                </button>
                {resultado.tipo_documento !== '99' ? (
                  <button
                    type="button"
                    aria-pressed={formatoImpresion === 'a4'}
                    onClick={() => setFormatoImpresion('a4')}
                    className={`rounded-control border p-4 text-left transition-colors ${
                      formatoImpresion === 'a4'
                        ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-200'
                        : 'border-ink-200 bg-white hover:border-brand-300 hover:bg-brand-50/50'
                    }`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                        <FileText className="size-4 text-brand-600" aria-hidden="true" />
                        Comprobante A4
                      </span>
                      <span
                        className={`size-4 rounded-full border-2 ${
                          formatoImpresion === 'a4'
                            ? 'border-brand-600 bg-brand-600 ring-2 ring-white'
                            : 'border-ink-300'
                        }`}
                        aria-hidden="true"
                      />
                    </span>
                    <span className="mt-2 block text-xs leading-relaxed text-ink-500">
                      Formato completo para impresora convencional.
                    </span>
                  </button>
                ) : null}
              </div>
            </fieldset>
            {errorPdf ? <Alert variant="error" title="No se pudo abrir el PDF">{errorPdf}</Alert> : null}
            {resultado.tipo_documento !== '99' ? (
              <p className="text-xs leading-relaxed text-ink-500">
                El comprobante quedó registrado y pendiente de procesamiento electrónico.
              </p>
            ) : null}
          </div>
        </Dialog>
      ) : null}
      <Dialog
        open={resultadoModalAbierto && (resultadoCredito !== null || resultadoConversion !== null)}
        onClose={() => setResultadoModalAbierto(false)}
        title="Listo"
        description={descripcionResultado}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={volverAlComprobante}>
              Volver al comprobante
            </Button>
            <Button
              leftIcon={<Printer className="size-4" aria-hidden="true" />}
              onClick={() => void abrirTicketResultado()}
            >
              Abrir ticket
            </Button>
          </>
        }
      >
        <p className="text-sm leading-relaxed text-ink-700">{mensajeResultado}</p>
      </Dialog>
      <Dialog
        open={dialogoDireccionAbierto}
        onClose={() => {
          if (!actualizarDireccion.isPending) setDialogoDireccionAbierto(false);
        }}
        title="Actualizar dirección del cliente"
        description={
          clienteActual
            ? `${clienteActual.nombre} · ${clienteActual.numero_documento}`
            : 'Edita la dirección registrada del cliente.'
        }
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setDialogoDireccionAbierto(false)}
              disabled={actualizarDireccion.isPending}
            >
              Cancelar
            </Button>
            <Button
              leftIcon={<CheckCircle2 className="size-4" aria-hidden="true" />}
              loading={actualizarDireccion.isPending}
              onClick={guardarDireccion}
            >
              Guardar dirección
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Dirección"
            value={direccionEditada}
            maxLength={250}
            onChange={(evento) => setDireccionEditada(evento.target.value)}
            placeholder="Ingresa la dirección del cliente"
            autoFocus
          />
          <p className="text-xs text-ink-400">
            Puedes dejar el campo vacío para quitar la dirección registrada.
          </p>
          {actualizarDireccion.isError ? (
            <Alert variant="error" title="No se pudo actualizar la dirección">
              {mensajeError(actualizarDireccion.error)}
            </Alert>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
}
